import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const api = axios.create({
  baseURL: BASE_URL,
});

// Track if we're currently refreshing the token to avoid multiple refresh attempts
let isRefreshing = false;
let failedQueue = [];

// Process the failed queue
const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Function to set the Authorization token
const setAuthToken = (token) => {
  if (token) {
    // Store token in localStorage
    localStorage.setItem("jwtToken", token);
    // Set token in the Axios instance headers
    api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    // Remove token from localStorage
    localStorage.removeItem("jwtToken");
    // Remove token from Axios instance headers
    delete api.defaults.headers.common["Authorization"];
  }
};

// Function to get fresh token from Firebase
const getFreshToken = async () => {
  try {
    // Import Firebase auth dynamically to avoid circular dependencies
    const { getAuth } = await import('firebase/auth');
    const auth = getAuth();
    
    if (auth.currentUser) {
      return await auth.currentUser.getIdToken(true);
    }
    return null;
  } catch (error) {
    console.error('Error getting fresh token:', error);
    return null;
  }
};

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Check if the error is due to authentication (401 or 403)
    if ((error.response?.status === 401 || error.response?.status === 403) && !originalRequest._retry) {
      if (isRefreshing) {
        // If we're already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await getFreshToken();
        
        if (newToken) {
          setAuthToken(newToken);
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          processQueue(null, newToken);
          return api(originalRequest);
        } else {
          // No valid user, redirect to login
          processQueue(new Error('No valid user'), null);
          window.location.href = '/login';
          return Promise.reject(error);
        }
      } catch (refreshError) {
        processQueue(refreshError, null);
        // Clear auth and redirect to login
        setAuthToken(null);
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Request interceptor to ensure we always have the latest token
api.interceptors.request.use(
  async (config) => {
    const token = localStorage.getItem("jwtToken");
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Retrieve token from localStorage and set it in Axios instance headers if it exists
const token = localStorage.getItem("jwtToken");
if (token) {
  setAuthToken(token);
}

export { api, setAuthToken };

export const createClient = (clientData) => api.post('v1/client/create', clientData);

export const createAssistant = (assistantData) => {
  console.log('Sending assistant data to API:', assistantData);
  return api.post('v1/assistant/create', assistantData, {
    headers: {
      'Content-Type': 'application/json'
    }
  });
};

const CHAT_AUTH = process.env.REACT_APP_API_AUTH_KEY;

export const startChat = () => api.get('v1/chat/start');
export const sendChatMessage = (messageData) => api.post('v1/chat/', messageData);
export const sendDealerChat = (messageData) =>
  api.post('v1/chat/dealer_chat', messageData, {
    params: { authorization: CHAT_AUTH },
    timeout: 120000,
  });
/** V3 Agent chat — same auth as dealer_chat; agents-first load path. */
export const sendAgentChat = (messageData) =>
  api.post('v1/agent/chat', messageData, {
    params: { authorization: CHAT_AUTH },
    timeout: 120000,
  });
export const setFirebaseUser = (userData) => api.post('v1/firestore/set_fb_user', userData);

export const createScrapingSettings = (settingsData, assistantId) => {
  return api.post('v1/firestore/create_scraping', settingsData, {
    params: { assistant_id: assistantId }
  });
};

export const generateDocumentation = (companyUrl) => api.post(`v1/firecrawl/generate_documentation?company_url=${companyUrl}`);

export const getAllConversations = () => api.get('v1/firestore/get_all_conversations');
export const deleteConversation = (threadId) => api.post(`v1/firestore/delete_conversation?thread_id=${threadId}`);
export const getLeads = (clientId) => api.get(`v1/firestore/get_leads?client_id=${clientId}`);
export const getDealerLeads = (assistantId) => api.get(`v1/dealers/get_dealer_leads?assistant_id=${assistantId}`);
export const triggerCaptureLead = (leadEmail, leadName, leadPhone, assistantId, threadId, authorization) => 
  api.post(`v1/tools/capture_lead?lead_email=${leadEmail}&lead_name=${leadName}&phone_number=${encodeURIComponent(leadPhone)}&assistant_id=${assistantId}&thread_id=${threadId}&authorization=${authorization}`);

export const triggerCaptureActivix = (leadPhone, leadName, assistantId, threadId, eventType, eventDate) =>
  api.post(`v1/tools/capture_activix?lead_phone=${leadPhone}&lead_name=${leadName}&assistant_id=${assistantId}&thread_id=${threadId}&event_type=${eventType}&event_date=${eventDate}`);
export const triggerCaptureADF = (leadPhone, leadName, assistantId, threadId) => 
  api.post(`v1/tools/capture_adf?lead_phone=${leadPhone}&lead_name=${leadName}&assistant_id=${assistantId}&thread_id=${threadId}`);

export const sendEmailTool = (leadName, threadId, toEmail, leadPhone = null, leadEmail = null) =>
  api.post(`v1/tools/send_email_tool?lead_name=${leadName}&thread_id=${threadId}&to_email=${toEmail}${leadPhone ? `&lead_phone=${encodeURIComponent(leadPhone)}` : ''}${leadEmail ? `&lead_email=${encodeURIComponent(leadEmail)}` : ''}`);
  
// Inventory related API calls
export const listAssistantIds = () => api.get('v1/inventory/list-assistant-ids');
export const getFullInventory = (assistantId) => api.get(`v1/inventory/get-full-inventory/${assistantId}`);

// Dealer Factory related API calls
export const setDealerAssistant = (assistantData) => api.post(
  'v1/assistant/set_dealer_assistant',
  assistantData,
  {
    headers: { 'Content-Type': 'application/json' },
    transformRequest: [(data) => JSON.stringify(data)],
  }
);
export const getAllDealerAssistant = () => api.get('v1/assistant/get_all_dealer_assistants');
export const deleteDealerAssistant = (assistantId) => api.delete(`v1/assistant/delete_dealer_assistant?assistant_id=${assistantId}`);
export const deleteInventory = (assistantId) => api.delete(`v1/inventory/delete_inventory?assistant_id=${assistantId}`);
export const deleteScrapeJob = (assistantId) => api.delete(`v1/inventory/delete_scrape_job?assistant_id=${assistantId}`);
export const getCmsData = () => api.get('v1/data/get_cms');
export const getModelsData = () => api.get('v1/data/get_models');
export const getImagesData = () => api.get('v1/data/get_images');
export const getMobileImagesData = () => api.get('v1/data/get_mobile_images');
export const getImageDimensionsData = () => api.get('v1/data/get_image_dimensions');
export const getIconsData = () => api.get('v1/data/get_icons');
export const getColorsData = () => api.get('v1/data/get_colors');
export const getToolsData = () => api.get('v1/data/get_tools');
export const getAutoOpenPagesData = () => api.get('v1/data/get_auto_open_pages');
export const getVfProjectsData = () => api.get('v1/data/get_vf_projects');
export const getCrmData = () => api.get('v1/data/get_crm_names');
export const getAdminsList = () => api.get('v1/data/get_admins_list');
export const createScrapeJob = (jobData) => api.post('v1/scraper/create_scrape_job', jobData);
export const startScrapeJob = (assistantId) => api.post(`v1/inventory/start-scrape-jobs?assistant_id=${assistantId}`);
export const convertLeadsToDealerLeads = (assistantId) => api.post(`v1/dealers/convert_leads_to_dealer_leads?assistant_id=${assistantId}`);


// Instructions related API calls
export const getAllInstructions = () => api.get('v1/dealers/get_all_instructions');
export const getInstructionIds = () => api.get('v1/firestore/get_instructions_ids');
export const setInstructions = (id, instructions) => api.post('v1/dealers/set_instructions', { id, instructions });
export const deleteInstructions = (id) => api.delete(`v1/dealers/delete_instructions?instructions_id=${id}`);
export const applyInstructions = (assistants, instructions_id) =>
  api.post('v1/dealers/apply_instructions', { assistants, instructions_id });

// Dealer Users related API calls
export const getAllDealerUsers = () => api.get('v1/dealers/get_all_dealer_users');
export const setDealerUser = (userData) => api.post('v1/dealers/set_dealer_user', userData);
export const deleteDealerUser = (userId) => api.delete(`v1/dealers/delete_dealer_user?firebase_uid=${userId}`);

// Send Batch related API calls
export const sendPhenixBatch = (assistantId, batchData) => api.post(`v1/chat/send_phenix_batch?assistant_id=${assistantId}`, batchData);

// URL Validation related API calls
export const checkInventoryUrls = (assistantId, maxWorkers = 20) =>
  api.get(`v1/inventory/check-urls/${assistantId}`, { params: { max_workers: maxWorkers } });

// Scrape Jobs Management API calls
export const getAllScrapeJobs = () => api.get('v1/firestore/get_all_documents', { params: { collection_name: 'scrape_jobs' } });

export const createScrapeJobApi = (jobData) => {
  const params = new URLSearchParams();
  params.append('assistant_id', jobData.assistant_id);
  params.append('cms_type', jobData.cms_type);
  params.append('dealer_url', jobData.dealer_url);
  params.append('status', jobData.status || 'active');
  if (jobData.extra_pages) {
    params.append('extra_pages', JSON.stringify(jobData.extra_pages));
  }
  return api.post(`v1/inventory/create-scrape-job?${params.toString()}`);
};

export const updateScrapeJobApi = (assistantId, updates) => {
  const params = new URLSearchParams();
  if (updates.cms_type) params.append('cms_type', updates.cms_type);
  if (updates.dealer_url) params.append('dealer_url', updates.dealer_url);
  if (updates.status) params.append('status', updates.status);
  if (updates.extra_pages) params.append('extra_pages', JSON.stringify(updates.extra_pages));
  if (updates.auto_dealer_doc !== undefined) params.append('auto_dealer_doc', updates.auto_dealer_doc);
  if (updates.feed_url) params.append('feed_url', updates.feed_url);
  if (updates.sftp) params.append('sftp', JSON.stringify(updates.sftp));
  return api.put(`v1/inventory/update-scrape-job/${assistantId}?${params.toString()}`);
};

// Start all scrape jobs (no assistant_id = starts all active jobs)
export const startAllScrapeJobs = () => api.post('v1/inventory/start-scrape-jobs');

// SFTP Presets related API calls - fetch from data/sftp document
export const getSftpPresetsData = () => api.get('v1/firestore/get_document', {
  params: { collection_name: 'data', document_id: 'sftp' }
});

// TalkLive related API calls
export const getTwilioNumbers = () => api.get('v1/talklive/twilio/numbers');
export const configureTwilioWebhook = (data) => api.post('v1/talklive/twilio/configure', data);

// TalkLive Multi-Instance API calls
export const getTalkliveInstances = (assistantId) => api.get(`v1/talklive/instances/${assistantId}`);
export const createTalkliveInstance = (assistantId, data) => api.post(`v1/talklive/instances/${assistantId}`, data);
export const getTalkliveInstance = (assistantId, instanceId) => api.get(`v1/talklive/instances/${assistantId}/${instanceId}`);
export const updateTalkliveInstance = (assistantId, instanceId, data) => api.put(`v1/talklive/instances/${assistantId}/${instanceId}`, data);
export const deleteTalkliveInstance = (assistantId, instanceId) => api.delete(`v1/talklive/instances/${assistantId}/${instanceId}`);

// ADF Config related API calls
export const getAdfPresets = () => api.get('v1/adf/presets');
export const getAdfPreset = (presetId) => api.get(`v1/adf/presets/${presetId}`);
export const createAdfPreset = (presetId, data) => api.post(`v1/adf/presets?preset_id=${encodeURIComponent(presetId)}`, data);
export const updateAdfPreset = (presetId, data) => api.put(`v1/adf/presets/${encodeURIComponent(presetId)}`, data);
export const deleteAdfPreset = (presetId) => api.delete(`v1/adf/presets/${encodeURIComponent(presetId)}`);
export const getAdfConfigs = () => api.get('v1/adf/configs');
export const getAdfConfig = (assistantId) => api.get(`v1/adf/configs/${encodeURIComponent(assistantId)}`);
export const createAdfConfig = (data) => api.post('v1/adf/configs', data);
export const updateAdfConfig = (assistantId, data) => api.put(`v1/adf/configs/${encodeURIComponent(assistantId)}`, data);
export const deleteAdfConfig = (assistantId) => api.delete(`v1/adf/configs/${encodeURIComponent(assistantId)}`);
export const previewAdf = (data) => api.post('v1/adf/preview', data);

// Marketplace settings API calls
export const getMarketplaceSettings = (assistantId) => api.get('v1/marketplace/get-marketplace-settings', {
  params: { assistant_id: assistantId }
});
export const setMarketplaceSettings = (settingsData) => api.post('v1/marketplace/set-marketplace-settings', settingsData);
export const getMarketplaceVmStatus = () => api.get('v1/marketplace/vm-status');
export const getMarketplaceVmLogs = (assistantId) => api.get('v1/marketplace/vm-logs', {
  params: { assistant_id: assistantId },
});

// Agent V3 API calls (nested Agent shape)
export const listAgents = () => api.get('v1/agent/list');
export const getAgentV3 = (assistantId) =>
  api.get('v1/agent/get_v3', { params: { assistant_id: assistantId } });
export const setAgentV3 = (agentData) =>
  api.post('v1/agent/set', agentData, {
    headers: { 'Content-Type': 'application/json' },
    transformRequest: [(data) => JSON.stringify(data)],
  });
export const deleteAgentV3 = (assistantId, deleteLegacy = false) =>
  api.delete('v1/agent/delete', {
    params: { assistant_id: assistantId, delete_legacy: deleteLegacy },
  });